import "./UpdateOrderDirective.scss";
import {IAttributes, ICompileService, IRootElementService, IScope, ITimeoutService} from "angular";
import {ITooltipProvider} from "angular-ui-bootstrap";
import {Optional} from "../../js/util/Optional";
import {OrderService} from "../services/OrderService";
import {OrderViewModel} from "../models/OrderViewModel";
import {AmendOrderRecord} from "../models/AmendOrderRecord";
import {isIressOrderModel} from "../models/iress/IressOrderViewModel";
import {isSimulatedOrderModel} from "app/orders/models/simulated/SimulatedOrderViewModel";
import {AccountBriefRecord} from "../../account/types/AccountBriefRecord";

export const OrderUpdate = "orderUpdate";

const template = `
    <div class="popover order-popover">
        <div class="arrow"></div>
        <div class="popover-inner">
            <div class="popover-content">
                <tf-update-order model="orderUpdate"
                                 account="accountUpdate"
                                 on-change="onChange(change)"
                                 on-close="closePopover()"
                                 on-cancel="onCancel()">
                </tf-update-order>
            </div>
        </div>
    </div>
`;

interface DirectiveScope extends IScope {
    orderUpdate: OrderViewModel;
    accountUpdate: AccountBriefRecord;
    popupPlacement: string;
    attachToBody: boolean;
    popoverClass: string;
    popupOpen: boolean;
    onCancel: () => void;
    closePopover: () => void;
    onChange: (change: AmendOrderRecord) => void;
}

/** @ngInject */
export function orderUpdate($compile: ICompileService,
                            $uibTooltip: ITooltipProvider,
                            $uibPosition: any,
                            orderService: OrderService,
                            $timeout: ITimeoutService) {

    /* Pre-Compile the template right at the start */
    const linker = $compile(template);

    return {
        restrict: "A",
        scope: {
            orderUpdate: "<",
            accountUpdate: "<",
            attachToBody: "<",
            popupOpen: "<",
            popoverClass: "@",
            popupPlacement: "@"
        },
        link
    };

    /** @ngInject */
    function link($scope: DirectiveScope, $elem: IRootElementService, $attr: IAttributes) {

        const attachToBody = Optional.of($scope.attachToBody).orElse(true);

        let visible = false;
        let popover: JQuery | null = null;
        let popoverScope: IScope | null;

        function compile(): Promise<JQuery> {
            return new Promise((resolve) => {
                popoverScope = $scope.$new();
                linker(popoverScope, (el: JQuery) => {
                    if (attachToBody) {
                        angular.element(document.body).append(el);
                    } else {
                        $elem.append(el);
                    }
                    el.addClass($scope.popupPlacement);
                    el.addClass($scope.popoverClass);
                    document.addEventListener("click", outsideClick);
                    resolve(el);
                });
            });
        }

        const watch = $scope.$watch("popupOpen", (newVal) => {
            if (newVal === true) {
                show();
            } else {
                hide();
            }
        });
        $scope.$on("$destroy", () => watch());

        function clickFromPopover($event: JQueryEventObject) {
            const target = $event.target;
            return popover != null && popover[0].contains(target);
        }

        $elem.on("click", ($event: JQueryEventObject) => {
            if ($elem[0].contains($event.target) && !clickFromPopover($event)) {
                show();
            }
        });

        function outsideClick($event: JQueryEventObject) {
            if (!clickFromPopover($event) && !$elem[0].contains($event.target)) {
                const event = popoverScope!.$broadcast("outsideClick");
                if (!event.defaultPrevented) {
                    hide();
                }
            }
        }

        async function show() {
            if (visible) {
                hide();
            } else {
                popover = await compile();
                $timeout(() => {
                    popover!.css("display", "block");
                    const position = $uibPosition.positionElements($elem, popover, $scope.popupPlacement, attachToBody);
                    popover!.css("left", position.left);
                    popover!.css("top", position.top + "px");
                    visible = true;
                });
            }
        }

        function hide() {
            Optional.of(popover).ifPresent(p => {
                p.css("display", "none");
                p.remove();
            });

            Optional.of(popoverScope)
                    .ifPresent(p => p.$destroy());

            document.removeEventListener("click", outsideClick);
            popoverScope = null;
            visible = false;
            popover = null;
        }

        $scope.$on("$destroy", () => hide());

        $scope.onChange = async (change: AmendOrderRecord) => {
            const order = $scope.orderUpdate;
            if (isIressOrderModel(order)) {
                await orderService.updateOrder(order.serverName, order.orderNumber, change);
                hide();
            } else if (isSimulatedOrderModel(order)) {
                await orderService.updateSimulatedOrder(order.id, change);
                hide();
            } else {
                throw Error(`Unable to modify order of type ${typeof order}`);
            }
        };

        $scope.onCancel = async () => {
            const order = $scope.orderUpdate;
            if (isIressOrderModel(order)) {
                await orderService.cancelOrder(order.serverName, order.orderNumber);
                hide();
            } else if (isSimulatedOrderModel(order)) {
                await orderService.cancelSimulatedOrder(order.id);
                hide();
            } else {
                throw Error(`Unable to cancel order of type ${typeof order}`);
            }
        };

        $scope.closePopover = () => hide();
    }
}
